import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Warehouse, Search, XCircle, Plus, ChevronUp, ChevronDown, Gauge, Trash2 } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BRANDS, makeBrandResolver } from '@/config/stockBrands';
import { formatApiError } from '@/utils/apiError';

/**
 * Category list for one brand. Click a category to drill into its product
 * list. Admin/manager users see Add Product / Delete buttons from the
 * category page itself.
 *
 * A brand-scoped search bar sits above the category tiles — when active it
 * hides the tiles and shows a flat product list filtered to this brand only.
 */
export default function StockBrand() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const canEditCatalogue = user && (user.role === 'admin' || user.role === 'manager');
  const [sp] = useSearchParams();
  const brandKey = sp.get('key') || 'proepoxy';
  const brand = BRANDS.find(b => b.key === brandKey) || BRANDS[0];

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [limitsCat, setLimitsCat] = useState(null);
  const [limitsForm, setLimitsForm] = useState({ default_min_stock: '', default_max_stock: '', apply_to_existing: false });
  const [limitsBusy, setLimitsBusy] = useState(false);

  const openCatLimits = (c) => {
    setLimitsForm({
      default_min_stock: c.default_min_stock == null ? '' : String(c.default_min_stock),
      default_max_stock: c.default_max_stock == null ? '' : String(c.default_max_stock),
      apply_to_existing: false,
    });
    setLimitsCat(c);
  };

  const saveCatLimits = async () => {
    if (!limitsCat) return;
    const payload = {
      default_min_stock: limitsForm.default_min_stock === '' ? null : Number(limitsForm.default_min_stock),
      default_max_stock: limitsForm.default_max_stock === '' ? null : Number(limitsForm.default_max_stock),
      apply_to_existing: limitsForm.apply_to_existing,
    };
    if (payload.default_min_stock != null && (isNaN(payload.default_min_stock) || payload.default_min_stock < 0)) {
      toast.error('Min stock must be 0 or higher.');
      return;
    }
    if (payload.default_max_stock != null && (isNaN(payload.default_max_stock) || payload.default_max_stock < 0)) {
      toast.error('Max stock must be 0 or higher.');
      return;
    }
    setLimitsBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/categories/${encodeURIComponent(limitsCat.category)}/limits`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed to save category limits');
      const applied = data.applied_to_existing || 0;
      toast.success(
        `${limitsCat.category} defaults saved` +
        (applied ? ` · pushed to ${applied} existing SKU${applied === 1 ? '' : 's'}` : '')
      );
      setLimitsCat(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLimitsBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catsRes, prodRes] = await Promise.all([
        fetch(`${API_URL}/api/stock/categories`, { credentials: 'include' }),
        fetch(`${API_URL}/api/stock/products`, { credentials: 'include' }),
      ]);
      if (!catsRes.ok) throw new Error('Failed to load categories');
      if (!prodRes.ok) throw new Error('Failed to load products');
      setCategories(await catsRes.json());
      setProducts(await prodRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const resolveBrand = useMemo(() => makeBrandResolver(categories), [categories]);

  const visibleCategories = useMemo(
    () => categories.filter(c => resolveBrand(c.category) === brand.key),
    [categories, brand.key, resolveBrand]
  );

  // Optimistic reorder: shuffle the local categories array immediately, then
  // POST the new sequence. Roll back to server state if the request fails.
  const reorderCategoriesTo = async (orderedNames) => {
    setCategories(prev => {
      const inBrand = orderedNames.map(n => prev.find(c => c.category === n)).filter(Boolean);
      const otherBrands = prev.filter(c => resolveBrand(c.category) !== brand.key);
      return [...inBrand, ...otherBrands];
    });
    try {
      const res = await fetch(`${API_URL}/api/stock/categories/reorder`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: brand.key, ordered_names: orderedNames }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Reorder failed');
    } catch (e) {
      toast.error(e.message);
      await load();
    }
  };

  const moveCatUp = (idx) => {
    if (idx <= 0) return;
    const names = visibleCategories.map(c => c.category);
    [names[idx - 1], names[idx]] = [names[idx], names[idx - 1]];
    reorderCategoriesTo(names);
  };

  const moveCatDown = (idx) => {
    if (idx >= visibleCategories.length - 1) return;
    const names = visibleCategories.map(c => c.category);
    [names[idx], names[idx + 1]] = [names[idx + 1], names[idx]];
    reorderCategoriesTo(names);
  };

  // Admin/manager can nuke an empty category. If it still has SKUs the
  // backend refuses with a 400 and we surface the error.
  const deleteCategory = async (c) => {
    if (!window.confirm(
      c.product_count > 0
        ? `"${c.category}" still has ${c.product_count} SKU(s). Move or delete those first.`
        : `Delete category "${c.category}"? This can't be undone.`
    )) return;
    try {
      const res = await fetch(`${API_URL}/api/stock/categories/${encodeURIComponent(c.category)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiError(data) || 'Failed to delete category');
      }
      toast.success(`${c.category} deleted`);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Brand-scoped product search. Same matching as the global one (name +
  // category) but pre-filtered to this brand so ProBound's "Resin Bound Stone"
  // hit doesn't pollute a search inside ProEpoxy.
  const searchResults = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return products.filter(p =>
      resolveBrand(p.category) === brand.key &&
      `${p.name} ${p.category}`.toLowerCase().includes(q)
    );
  }, [products, brand.key, searchTerm, resolveBrand]);

  const isSearching = searchTerm.trim().length > 0;

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff/stock')} data-testid="brand-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Current Stock">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Warehouse className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> {brand.label}</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">{brand.blurb}</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5">
        {/* Brand-scoped search + Add category — replaces the category tiles when searching. */}
        <div className="flex items-center gap-2 mb-5">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              data-testid="stock-brand-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${brand.label} products…`}
              className="pl-9 pr-10 h-11"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                data-testid="stock-brand-search-clear"
                aria-label="Clear search"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>
          {canEditCatalogue && (
            <Button
              onClick={() => setShowAddCat(true)}
              data-testid="stock-brand-add-category"
              className="shrink-0 h-11 text-white"
              style={{ background: brand.color }}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Add category</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : isSearching ? (
          searchResults.length === 0 ? (
            <Card className="p-10 text-center text-gray-500 italic">
              No {brand.label} products match &ldquo;{searchTerm}&rdquo;.
            </Card>
          ) : (
            <>
              <div className="text-xs text-gray-500 mb-2 px-1">
                {searchResults.length} {brand.label} product{searchResults.length === 1 ? '' : 's'} matching &ldquo;{searchTerm}&rdquo;
              </div>
              <Card className="divide-y divide-gray-100">
                {searchResults.map(p => (
                  <button
                    key={p.id}
                    data-testid={`stock-brand-search-row-${p.id}`}
                    onClick={() => navigate(`/staff/stock/category?name=${encodeURIComponent(p.category)}`)}
                    className="w-full p-3 sm:p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    {/* Mobile: stack so the product name gets full width — the
                        brand pill can otherwise chew up 40% of the row on a
                        390px screen and force "Structural Reinforce…" to
                        truncate. Desktop layout unchanged. */}
                    <div className="flex items-start justify-between gap-3 sm:hidden">
                      <div className="font-semibold text-gray-900 leading-tight break-words min-w-0 flex-1">
                        {p.name}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] uppercase text-gray-500 leading-none">On hand</div>
                        <div className="text-lg font-bold leading-tight">{p.qty_on_hand}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 sm:hidden">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                        style={{ background: brand.color, color: brand.ink }}
                      >
                        {brand.label}
                      </span>
                      <span className="text-xs text-gray-500 truncate">{p.category}</span>
                    </div>

                    <div className="hidden sm:flex items-center gap-3">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                        style={{ background: brand.color, color: brand.ink }}
                      >
                        {brand.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                        <div className="text-xs text-gray-500 truncate">{p.category}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase text-gray-500">On hand</div>
                        <div className="text-xl font-bold">{p.qty_on_hand}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </Card>
            </>
          )
        ) : visibleCategories.length === 0 ? (
          <div className="text-center py-16 text-gray-500 italic">No categories yet under {brand.label}.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visibleCategories.map((c, idx) => (
              <div
                key={c.category}
                className="relative rounded-xl border-2 border-transparent bg-white hover:border-gray-300 hover:shadow transition-all"
              >
                <button
                  data-testid={`stock-cat-${c.category}`}
                  onClick={() => navigate(`/staff/stock/category?name=${encodeURIComponent(c.category)}`)}
                  className={`w-full p-4 text-left ${canEditCatalogue ? 'pr-14' : ''}`}
                >
                  <div className="text-sm font-bold text-gray-900">{c.category}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{c.product_count} SKUs · {c.total_qty} units on hand</div>
                  {(c.default_min_stock != null || c.default_max_stock != null) && (
                    <div className="text-[10px] text-gray-500 mt-1" data-testid={`stock-cat-defaults-${c.category}`}>
                      Defaults · Min {c.default_min_stock ?? '—'} / Max {c.default_max_stock ?? '—'}
                    </div>
                  )}
                </button>
                {canEditCatalogue && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openCatLimits(c); }}
                    data-testid={`stock-cat-limits-${c.category}`}
                    className="absolute right-2 top-2 w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100 active:bg-gray-200 text-gray-600 shadow-sm"
                    aria-label={`Set default stock levels for ${c.category}`}
                    title="Set default min/max for new SKUs in this category"
                  >
                    <Gauge className="w-4 h-4" />
                  </button>
                )}
                {canEditCatalogue && (
                  <div className="absolute right-2 bottom-2 flex gap-1.5">
                    {visibleCategories.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveCatUp(idx); }}
                          disabled={idx === 0}
                          data-testid={`stock-cat-up-${c.category}`}
                          className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed text-gray-700 shadow-sm"
                          aria-label={`Move ${c.category} up`}
                        >
                          <ChevronUp className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveCatDown(idx); }}
                          disabled={idx === visibleCategories.length - 1}
                          data-testid={`stock-cat-down-${c.category}`}
                          className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed text-gray-700 shadow-sm"
                          aria-label={`Move ${c.category} down`}
                        >
                          <ChevronDown className="w-5 h-5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteCategory(c); }}
                      disabled={c.product_count > 0}
                      data-testid={`stock-cat-delete-${c.category}`}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-600 active:bg-rose-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-500 shadow-sm"
                      aria-label={`Delete ${c.category}`}
                      title={c.product_count > 0
                        ? `Move or delete the ${c.product_count} SKU(s) in "${c.category}" first`
                        : `Delete "${c.category}"`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddCat && (
        <AddCategoryModal
          brand={brand}
          onClose={() => setShowAddCat(false)}
          onDone={async () => { setShowAddCat(false); await load(); }}
        />
      )}

      <Dialog open={!!limitsCat} onOpenChange={(o) => !o && !limitsBusy && setLimitsCat(null)}>
        <DialogContent data-testid="stock-cat-limits-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set default stock levels</DialogTitle>
            <DialogDescription>
              <strong>{limitsCat?.category}</strong> · these defaults apply to <strong>new SKUs</strong> added to this category. Existing products keep whatever limits they already have unless you tick the box below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cat-limit-min">Default min stock</Label>
              <Input
                id="cat-limit-min"
                data-testid="stock-cat-limit-min-input"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={limitsForm.default_min_stock}
                onChange={(e) => setLimitsForm(f => ({ ...f, default_min_stock: e.target.value }))}
                placeholder="e.g. 5"
                className="mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="cat-limit-max">Default max stock</Label>
              <Input
                id="cat-limit-max"
                data-testid="stock-cat-limit-max-input"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={limitsForm.default_max_stock}
                onChange={(e) => setLimitsForm(f => ({ ...f, default_max_stock: e.target.value }))}
                placeholder="e.g. 100"
                className="mt-1"
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer pt-1">
              <Checkbox
                checked={limitsForm.apply_to_existing}
                onCheckedChange={(v) => setLimitsForm(f => ({ ...f, apply_to_existing: !!v }))}
                data-testid="stock-cat-limits-apply-existing"
              />
              <span className="text-[13px] leading-snug">
                Also apply to existing SKUs in this category that don&apos;t already have their own limits set
              </span>
            </label>
            <p className="text-[11px] text-gray-500">Leave a field blank to clear that default. Per-SKU customisations always win over category defaults.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitsCat(null)} disabled={limitsBusy}>Cancel</Button>
            <Button onClick={saveCatLimits} disabled={limitsBusy} data-testid="stock-cat-limits-save" className="bg-slate-800 hover:bg-slate-900 text-white">
              {limitsBusy ? 'Saving…' : 'Save defaults'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddCategoryModal({ brand, onClose, onDone }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = name.trim();
    if (!n) { toast.error('Enter a category name.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/categories`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: n, brand: brand.key }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      toast.success(`Created category "${n}" under ${brand.label}.`);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Add category</h2>
          <p className="text-xs text-gray-500 mt-1">Brand: <strong>{brand.label}</strong></p>
        </div>
        <div className="p-5">
          <Label htmlFor="addcat-name" className="text-sm font-semibold">Category name</Label>
          <Input
            id="addcat-name"
            data-testid="add-category-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Diamond Tooling, Specialist Resins"
            className="mt-1"
            autoFocus
          />
          <p className="text-[11px] text-gray-500 mt-2">This will be empty to start with — add products to it from inside the category.</p>
        </div>
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="add-category-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={busy}
            data-testid="add-category-submit"
            className="text-white"
            style={{ background: brand.color }}
          >
            {busy ? 'Saving…' : 'Create category'}
          </Button>
        </div>
      </div>
    </div>
  );
}
